<template>
  <a-modal
    :visible="visible"
    @ok="handleOk"
    @cancel="handleCancel"
    :width="1059"
    wrapClassName="LogisticsContainer"
    :footer="null"
    :centered="true"
    :bodyStyle="{ height: '690px', padding: 0 }"
  >
    <template #title>
      <div>
        物流详情
        <span class="split" v-if="orderList?.length > 0">
          (处方被拆分成
          <span class="bold">{{ orderList?.length }}</span>
          个订单)
        </span>
      </div>
    </template>
    <div class="body">
      <!-- 订单列表 -->
      <div class="order-list">
        <div
          class="order-box"
          :class="item.orderId === activeKey && 'selected'"
          v-for="(item, index) in orderList"
          :key="item.orderId"
          @click="handleSelect(item.orderId)"
        >
          <div class="order-number">{{ `订单 ${index + 1}` }}</div>
          <div class="img-box">
            <a-image-preview-group>
              <div
                :key="skuIndex"
                class="img-item"
                v-for="(skuItem, skuIndex) in item.skuInfoList"
                :style="{ display: skuIndex > 1 ? 'none' : 'block' }"
              >
                <a-image :src="skuItem.url" />
                <div v-if="item.skuInfoList?.length > 2 && skuIndex === 1" class="mask">
                  {{ ` +${item.skuInfoList?.length - 2} ` }}
                </div>
              </div>
            </a-image-preview-group>
          </div>
        </div>
      </div>
      <!-- 订单信息 -->
      <div class="order-info">
        <div
          class="info-box"
          v-for="(item, index) in infoList"
          :key="index"
          :style="{ borderRight: index + 1 === infoList.length ? '0px' : '1px solid #e8e8e8' }"
        >
          <span class="label">{{ `${item.label}：` }}</span>
          <span class="val">{{ logisticsInfo[item.value] || '--' }}</span>
        </div>
      </div>
      <!-- 物流信息 -->
      <div
        class="logistics-info"
        :class="{ 'can-fold': showFoldButton, fold: isFold && showFoldButton }"
        ref="logisticsInfoRef"
        :style="{ maxHeight: getMaxHeight }"
        v-if="logisticsInfo?.orderRouteInfoList && logisticsInfo?.orderRouteInfoList?.length > 0"
      >
        <a-timeline>
          <a-timeline-item
            v-for="(logisticsItem, logisticsIndex) in logisticsInfo?.orderRouteInfoList"
            :key="logisticsIndex"
            :class="index === 0 && 'current'"
          >
            <template #dot>
              <div class="dot"></div>
            </template>
            <div class="status">{{ logisticsItem.statusName }}</div>
            <div class="describe">{{ logisticsItem.content }}</div>
            <div class="time">{{ logisticsItem.createdAt }}</div>
          </a-timeline-item>
        </a-timeline>
        <div
          class="fold-text"
          :class="isFold ? 'fold' : 'expand'"
          v-if="showFoldButton"
          @click="handleFold"
        >
          {{ `${isFold ? '展开' : '收起'}全部` }}
          <img :src="expand" alt="" class="icon" />
        </div>
      </div>
      <div v-else class="empty">暂无物流信息</div>
      <!-- 药品列表 -->
      <div class="medicines-title">
        <span>药品列表</span>
        <span
          v-if="Unshipped.data.showUrgeDeliveryContent"
          @click="openUnshippedModal()"
          class="link"
        >
          未发货？点此了解
        </span>
      </div>
      <div
        class="medicines-list"
        v-if="logisticsInfo.skuInfoList && logisticsInfo.skuInfoList?.length > 0"
      >
        <div
          class="medicines-box"
          v-for="(skuItem, skuIndex) in logisticsInfo.skuInfoList"
          :key="skuIndex"
        >
          <div class="medicines-img">
            <a-image :src="skuItem.url" alt="" />
          </div>
          <div class="medicines-right">
            <div class="medicines-name">{{ skuItem.skuName }}</div>
            <div class="medicines-price">
              <div>
                <span class="unit">¥</span>
                {{ skuItem.skuPrice.toFixed(2) }}
              </div>
              <div v-if="skuItem.quantity">{{ `x ${skuItem.quantity}` }}</div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="empty">暂无药品信息</div>
      <!-- 操作日志 -->
      <OperationLog ref="operationLogRef" :orderSn="logisticsInfo.orderSn" />
      <!-- 使用独立的未发货弹窗组件 -->
      <UnshippedModal
        @onUrge="handleUrge"
        v-model:visible="Unshipped.show"
        :info="logisticsInfo"
        :detail="Unshipped.data"
      />
    </div>
  </a-modal>
</template>

<script setup>
import {
  ref,
  defineExpose,
  watch,
  nextTick,
  computed,
  defineProps,
  reactive,
  createVNode
} from 'vue'
import { infoList } from './config'
import expand from '@/assets/img/expand.png'
import { getLogistics, getLogisticsAbnormal, urgeDelivery } from '@/api/patientFile'
import OperationLog from './OperationLog.vue'
import UnshippedModal from './UnshippedModal/index.vue'
import { message, Modal } from 'ant-design-vue'
import { ExclamationCircleOutlined } from '@ant-design/icons-vue'

const props = defineProps({
  prescriptionSn: {
    type: String,
    default: ''
  }
})
const logisticsInfoRef = ref(null)
const visible = ref(false)
const activeKey = ref(1)
const isFold = ref(true)
const contentHeight = ref(0)
const showFoldButton = ref(false)
const orderList = ref([]) // 订单列表
const logisticsInfo = ref({}) // 物流信息
const operationLogRef = ref()
const Unshipped = reactive({
  show: false,
  data: {}
})

watch(
  () => visible.value,
  val => {
    if (val) {
      getLogisticsDetails()
    }
  }
)

watch(
  () => activeKey.value,
  val => {
    const curOrder = orderList.value.filter(item => item.orderId === val)
    logisticsInfo.value = curOrder[0] || {}
    handleUrge()
  },
  { immediate: true }
)

// 获取物流信息
const getLogisticsDetails = async () => {
  const res = await getLogistics({ prescriptionSn: props.prescriptionSn })
  orderList.value = res || [{}]
  handleSelect(res[0]?.orderId)
}

const handleCancel = () => {
  visible.value = false
}
const handleOk = () => {}

// 计算最大高度
const getMaxHeight = computed(() => {
  if (!showFoldButton.value) {
    // 不需要折叠按钮时，高度自适应
    return 'none'
  } else if (isFold.value) {
    // 需要折叠且当前是折叠状态
    return '313px'
  } else {
    // 需要折叠且当前是展开状态
    return `${contentHeight.value + 50}px` // 50px为按钮高度预留
  }
})

// 计算内容高度
const calculateContentHeight = () => {
  nextTick(() => {
    if (!logisticsInfoRef.value) return

    // 先设置为auto获取实际高度
    logisticsInfoRef.value.style.maxHeight = 'none'
    const height = logisticsInfoRef.value.scrollHeight

    // 判断是否需要显示折叠按钮
    showFoldButton.value = height > 313

    // 保存内容高度
    contentHeight.value = height

    // 如果需要折叠按钮且当前是折叠状态，设置回折叠高度
    if (showFoldButton.value && isFold.value) {
      logisticsInfoRef.value.style.maxHeight = '313px'
    } else {
      logisticsInfoRef.value.style.maxHeight = 'none'
    }
  })
}

// 监听物流列表变化
watch(
  () => logisticsInfo.value?.orderRouteInfoList,
  () => {
    calculateContentHeight()
  },
  { deep: true }
)

// 监听modal显示状态
watch(visible, newVal => {
  if (newVal) {
    nextTick(() => {
      calculateContentHeight()
    })
  }
})

// 选择订单
const handleSelect = id => {
  activeKey.value = id
}

// 物流信息展开收起
const handleFold = () => {
  isFold.value = !isFold.value
}
function handleUrge() {
  nextTick(() => {
    // 刷新操作日志
    operationLogRef.value?.getLog()
  })
  Unshipped.data = {}
  if (!logisticsInfo.value?.orderSn) return
  const params = {
    orderSn: logisticsInfo.value.orderSn,
    skuCodeList: logisticsInfo.value.skuInfoList?.map(v => v.skuCode) || []
  }
  getLogisticsAbnormal(params).then(res => {
    Unshipped.data = res || {}
  })
}
const openUnshippedModal = () => {
  const detail = Unshipped.data
  if (detail.handleType === 2) {
    Modal.confirm({
      title: () => '提醒',
      icon: () => createVNode(ExclamationCircleOutlined),
      content: () => detail.tipMessage,
      okText: '督促发货',
      cancelText: '取消',
      onOk() {
        const skuCodeList = logisticsInfo.value.skuInfoList?.map(v => v.skuCode)
        const params = {
          handleType: detail.handleType,
          orderSn: logisticsInfo.value?.orderSn,
          urgeReason: '',
          skuCodeList
        }
        urgeDelivery(params).then(() => {
          message.success('催促发货已发送')
          handleUrge()
        })
      },
      onCancel() {}
    })
  } else {
    Unshipped.show = true
  }
}
defineExpose({
  visible
})
</script>

<style scoped lang="scss">
.LogisticsContainer {
  .split {
    color: #999;
    font-size: 14px;
    font-weight: 400;
    .bold {
      font-weight: 500;
    }
  }
  .body {
    height: 100%;
    padding: 16px 24px;
    overflow: auto;
    .order-list {
      display: flex;
      margin-bottom: 20px;
      .order-box {
        position: relative;
        display: flex;
        align-items: center;
        padding: 8px 16px;
        border-radius: 4px;
        color: #333;
        border: 1px solid #efefef;
        margin-right: 12px;
        cursor: pointer;

        ::v-deep(.ant-image) {
          width: 48px;
          height: 48px;
          margin-left: 8px;
          border-radius: 8px;
          background: #fff;
          border: 0.5px solid #efefef;
          box-sizing: border-box;
          &:nth-child(2) {
            margin-right: 15px;
          }
        }

        .order-number {
          font-weight: 500;
          font-size: 16px;
          margin-right: 6px;
        }
        .img-box {
          display: flex;
          .img-item {
            position: relative;
          }
          .mask {
            position: absolute;
            display: flex;
            align-items: center;
            justify-content: center;
            left: 8px;
            top: 0;
            background: #21212199;
            width: 48px;
            height: 48px;
            border-radius: 8px;
            font-weight: 500;
            font-size: 16px;
            color: #fff;
            pointer-events: none;
          }
        }
      }
      .selected {
        background-color: rgba(24, 144, 255, 0.08);
        border: 1px solid #1890ff;
        color: #1890ff;
        ::v-deep(.ant-image) {
          border: 0.5px solid transparent;
        }
      }
    }

    .order-info {
      display: flex;
      margin-bottom: 14px;
      .info-box {
        padding: 5px 34px 5px 0;
        margin-right: 16px;
        .label {
          color: #999;
          font-size: 14px;
          font-weight: 500;
        }
        .val {
          color: #333;
          font-size: 14px;
          font-weight: 500;
        }
      }
    }

    .logistics-info {
      position: relative;
      margin-bottom: 16px;
      padding-top: 10px;
      overflow: hidden;
      &.can-fold {
        // 使用GPU加速
        will-change: max-height;

        // 折叠状态
        &.fold {
          max-height: 313px;

          // 优化遮罩，避免影响性能
          .fold-mask {
            position: absolute;
            bottom: 40px; // 在按钮上方
            left: 0;
            right: 0;
            height: 40px;
            background: linear-gradient(
              180deg,
              rgba(255, 255, 255, 0) 0%,
              rgba(255, 255, 255, 0.9) 100%
            );
            pointer-events: none;
            opacity: 1;
            transition: opacity 0.2s ease;
            z-index: 1;
          }
        }

        // 展开状态
        &:not(.fold) {
          max-height: 800px; // 使用固定值而非none

          .fold-mask {
            opacity: 0;
          }
        }

        // 优化过渡动画
        transition: max-height 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94);

        // 确保内部内容不触发重排
        ::v-deep(.ant-timeline) {
          transform: translateZ(0); // 启用GPU加速
          backface-visibility: hidden;
          perspective: 1000px;
        }
      }
      ::v-deep(.ant-timeline-item) {
        padding: 0 0 13px;
      }
      ::v-deep(.ant-timeline-item-tail) {
        height: 100%;
      }
      ::v-deep(.ant-timeline-item-head-custom) {
        padding: 0;
      }
      .status {
        font-weight: 500;
        font-size: 16px;
        color: #808080;
        margin-bottom: 5px;
      }
      .describe {
        font-weight: 400;
        color: #808080;
        line-height: 22px;
        font-size: 14px;
        margin-bottom: 5px;
      }
      .time {
        font-weight: 400;
        font-size: 12px;
        color: #808080;
      }
      .dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #d8d8d8;
      }
      .current {
        .dot {
          background: #3f64ff;
        }
        .status {
          color: #1890ff;
        }
        .describe {
          color: #1c1c1c;
        }
      }
      .fold-text {
        display: flex;
        justify-content: center;
        align-items: center;
        font-weight: 400;
        font-size: 14px;
        color: #666;
        cursor: pointer;
        // 按钮本身的过渡
        transition: all 0.3s ease;

        .icon {
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        &.expand {
          .icon {
            transform: rotate(180deg);
          }
        }
      }
      .fold {
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        background-image: linear-gradient(180deg, #ffffff00 0%, #ffffff 100%);
        height: 76px;
      }
      .expand {
        position: relative;
      }
      // 为图标添加旋转动画
      .icon {
        width: 16px;
        height: 16px;
        margin-left: 4px;
        transition: transform 0.3s ease;
      }

      .expand {
        .icon {
          transform: rotate(180deg);
        }
      }

      .fold {
        .icon {
          transform: rotate(0deg);
        }
      }
    }

    .medicines-title {
      display: flex;
      justify-content: space-between;
      .link {
        font-size: 14px;
        color: #1890ff;
        cursor: pointer;
      }
      .title {
        font-weight: 500;
        font-size: 14px;
        color: #333333;
        margin-bottom: 12px;
      }
    }
    .medicines-list {
      display: flex;
      flex-wrap: wrap;
      .medicines-box {
        display: flex;
        padding: 12px 16px;
        width: 48%;
        border-radius: 4px;
        background: #fafafa;
        margin: 0 13px 13px 0;
        .medicines-img {
          width: 48px;
          height: 48px;
          border-radius: 8px;
          background: #fff;
          border: 0.5px solid #efefef;
          margin-right: 12px;
        }
        .medicines-right {
          flex: 1;
          .medicines-name {
            font-weight: 500;
            font-size: 16px;
            color: #333333;
          }
          .medicines-price {
            display: flex;
            justify-content: space-between;
            color: #333333;
            font-size: 14px;
            font-weight: 400;
            .unit {
              font-size: 10px;
            }
          }
        }
      }
    }
  }
  .empty {
    color: #999;
    font-size: 14px;
    margin-bottom: 12px;
    text-align: center;
    padding: 40px 0;
  }
}
</style>
